import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ColumnDef } from "@tanstack/react-table";
import type { RootState } from "../../../../../redux/store";
import { toggleCollapsedStageKey } from "../../../../../redux/slices/dealsSlice";
import CollapseArrowIcon from "../../../../../assets/svg/collapse-arrow-icon";
import SortByIcon from "../../../../../assets/svg/sort-by-icon";
import RightArrowIcon from "../../../../../assets/svg/right-arrow-icon";
import Table from "../../../../../components/Table/Table";
import Pagination from "../../../../../components/Table/Pagination";
import { Avatar } from "../../../../../components/Avatar/Avatar";
import { TruncatedTextTooltip } from "../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import { AgentAssignmentDropdown } from "../../AgentAssignmentDropdown";
import { ContactOwnerFilter } from "../../KanbanGrid/components/tableFilters/ContactOwnerFilter";
import { formatDealCurrency } from "../utils/functions";
import { DEFAULT_DEALS_PER_PAGE, DEAL_LABELS } from "../utils/constants";
import type { Deal, DealTableListingProps } from "../utils/types";
import InfoBtnIcon from "../../../../../assets/svg/info-btn-icon";
import { Tooltip } from "../../../../../components/Tooltip/Tooltip";
import { getCurrencySymbol } from "../../../../../utils/functions/get-currency-symbols";
import {
  useLazyGetDealsListQuery,
  useUpdateDealMutation,
} from "../../../../../redux/services/dealsApi";
import TableShimmer from "../../../../../components/Table/TableShimmer";
import { getDateTimeByFormat } from "../../../../../redux/slices/dateTimeFormatSlice";
import ChevronRightIcon from "../../../../../assets/svg/chevron-right-icon";
import { usePersistedPagination } from "../../../../../hooks/usePersistedPagination";
const FilterableHeader = ({
  label,
  filterKey,
  activeFilter,
  onToggleFilter,
  children,
}: {
  label: string;
  filterKey: string;
  activeFilter: string | null;
  onToggleFilter: (key: string) => void;
  children: (
    triggerRef: React.RefObject<HTMLDivElement | null>
  ) => React.ReactNode;
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  return (
    <div className="relative">
      <div
        ref={triggerRef}
        className="text-left px-3 py-2 flex items-center gap-1 cursor-pointer"
        data-table-filter-trigger
        onClick={(e) => {
          e.stopPropagation();
          onToggleFilter(filterKey);
        }}
      >
        <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
          {label}
        </span>
        <SortByIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
      </div>
      {activeFilter === filterKey && children(triggerRef)}
    </div>
  );
};

const SortableHeader = ({
  label,
  sortField,
  activeSortKey,
  activeSortOrder,
  onSort,
  onTogglePopper,
  isPopperOpen,
}: {
  label: string;
  sortField?: string;
  activeSortKey?: string;
  activeSortOrder?: "asc" | "desc";
  onSort?: (key: string, order: "asc" | "desc" | undefined) => void;
  onTogglePopper?: (key: string) => void;
  isPopperOpen?: boolean;
}) => {
  const isActive = sortField && activeSortKey === sortField;
  return (
    <div className="relative">
      <div
        className="text-left px-3 py-2 flex items-center gap-1 cursor-pointer select-none"
        data-sort-trigger
        onClick={(e) => {
          e.stopPropagation();
          if (sortField) onTogglePopper?.(sortField);
        }}
      >
        <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
          {label}
        </span>
        <SortByIcon
          className={`w-4 h-4 shrink-0 text-verba-blue dark:text-verba-blue-dk transition-transform ${
            isActive && activeSortOrder === "desc" ? "rotate-180" : ""
          }`}
        />
      </div>
      {isPopperOpen && sortField && (
        <div
          data-sort-popper
          className="absolute top-full left-0 z-50 mt-1 rounded-[8px] border border-border-gray dark:border-border-gray-dk bg-popper-bg/80 dark:bg-popper-bg-dk/80 backdrop-blur-[20px] shadow-[10px_10px_60px_0px_rgba(0,0,0,0.2)] p-[12px] flex flex-col gap-[16px]"
        >
          <div className="flex flex-col w-[130px]">
            <button
              className={`flex items-center gap-[4px] px-[8px] py-[8px] rounded-[8px] cursor-pointer w-full ${
                isActive && activeSortOrder === "asc"
                  ? "bg-hover-effect dark:bg-hover-effect-dk"
                  : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onSort?.(sortField, "asc");
                onTogglePopper?.(sortField);
              }}
            >
              <RightArrowIcon className="w-[18px] h-[18px] text-heading-text dark:text-heading-text-dk shrink-0 rotate-270" />
              <span className="text-md text-heading-text dark:text-heading-text-dk whitespace-nowrap">
                Sort ascending
              </span>
            </button>
            <button
              className={`flex items-center gap-[4px] px-[8px] py-[8px] rounded-[8px] cursor-pointer w-full ${
                isActive && activeSortOrder === "desc"
                  ? "bg-hover-effect dark:bg-hover-effect-dk"
                  : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onSort?.(sortField, "desc");
                onTogglePopper?.(sortField);
              }}
            >
              <RightArrowIcon className="w-[18px] h-[18px] text-heading-text dark:text-heading-text-dk shrink-0 rotate-90" />
              <span className="text-md text-heading-text dark:text-heading-text-dk whitespace-nowrap">
                Sort descending
              </span>
            </button>
          </div>
          <button
            className="text-xs font-semibold text-verba-blue dark:text-verba-blue-dk cursor-pointer text-left px-[8px] whitespace-nowrap"
            onClick={(e) => {
              e.stopPropagation();
              onSort?.(sortField, undefined);
              onTogglePopper?.(sortField);
            }}
          >
            Reset
          </button>
        </div>
      )}
    </div>
  );
};

const DealTableListing = ({
  stage,
  sharedOwnerIds = [],
  onOwnerApply,
  onOwnerReset,
  viewId,
  sortKey,
  sortOrder,
  onSort,
}: DealTableListingProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isViewSelected = !!viewId;
  const collapsedStageKeys = useSelector(
    (state: RootState) => state.deals.collapsedStageKeys
  );
  const isCollapsed = collapsedStageKeys?.includes(stage?.stage_key);

  const handleToggleCollapse = useCallback(() => {
    dispatch(toggleCollapsedStageKey(stage?.stage_key));
  }, [dispatch, stage?.stage_key]);

  const {
    page: currentPage,
    setPage: setCurrentPage,
    limit: rowsPerPage,
    setLimit: setRowsPerPage,
  } = usePersistedPagination(`crm-deal:${stage?.stage_key}`, {
    defaultLimit: DEFAULT_DEALS_PER_PAGE,
    resetOn: [sharedOwnerIds, sortKey, sortOrder],
  });
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(
    null
  );
  const [activeSortPopper, setActiveSortPopper] = useState<string | null>(null);
  const [pageDeals, setPageDeals] = useState<Deal[]>(stage?.deals ?? []);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [pageTotalCount, setPageTotalCount] = useState(stage?.total ?? 0);
  const [fetchDealsList] = useLazyGetDealsListQuery();
  const [updateDeal] = useUpdateDealMutation();

  const isPaginated =
    currentPage > 1 || rowsPerPage !== DEFAULT_DEALS_PER_PAGE;

  useEffect(() => {
    if (!isPaginated) {
      setPageDeals(stage?.deals ?? []);
      setPageTotalCount(stage?.total ?? 0);
    }
  }, [isPaginated, stage?.deals, stage?.total]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(pageTotalCount / rowsPerPage)),
    [pageTotalCount, rowsPerPage]
  );

  const fetchPage = useCallback(
    async (page: number, limit: number) => {
      setIsPageLoading(true);
      const hasUnassigned = sharedOwnerIds.includes("unassigned");
      const agentIds = sharedOwnerIds.filter((id) => id !== "unassigned");
      try {
        const result = await fetchDealsList({
          stage: stage.stage_key,
          page,
          limit,
          view_id: viewId,
          sort_key: sortKey,
          sort_order: sortOrder,
          assignee: agentIds.length > 0 ? agentIds : undefined,
          is_unassigned: hasUnassigned || undefined,
        }).unwrap();
        setPageDeals(result?.deals ?? []);
        setPageTotalCount(result?.total ?? 0);
      } catch {
        setPageDeals(
          (stage?.deals ?? []).slice((page - 1) * limit, page * limit)
        );
      } finally {
        setIsPageLoading(false);
      }
    },
    [
      fetchDealsList,
      stage.stage_key,
      stage.deals,
      viewId,
      sortKey,
      sortOrder,
      sharedOwnerIds,
    ]
  );

  useEffect(() => {
    if (!isPaginated) return;
    fetchPage(currentPage, rowsPerPage);
  }, [isPaginated, currentPage, rowsPerPage, fetchPage]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (page < 1 || page === currentPage) return;
      setCurrentPage(page);
    },
    [currentPage, setCurrentPage]
  );

  const handleRowsPerPageChange = useCallback(
    (size: number) => {
      setRowsPerPage(size);
      setCurrentPage(1);
    },
    [setRowsPerPage, setCurrentPage]
  );

  const handleScrollToggle = useCallback((lock: boolean) => {
    const containers = document.querySelectorAll(
      "[data-crm-stage-scroll-container]"
    );
    containers.forEach((el) => {
      if (lock) {
        (el as HTMLElement).style.overflowY = "hidden";
        (el as HTMLElement).style.overflowX = "hidden";
      } else {
        (el as HTMLElement).style.removeProperty("overflow-y");
        (el as HTMLElement).style.removeProperty("overflow-x");
      }
    });
  }, []);

  const handleToggleFilter = useCallback((col: string) => {
    setActiveFilterColumn((prev) => (prev === col ? null : col));
  }, []);

  const handleOwnerApply = useCallback(
    (ids: string[]) => {
      onOwnerApply?.(ids);
      setActiveFilterColumn(null);
      setCurrentPage(1);
    },
    [onOwnerApply]
  );

  const handleOwnerReset = useCallback(() => {
    onOwnerReset?.();
    setActiveFilterColumn(null);
    setCurrentPage(1);
  }, [onOwnerReset]);

  const handleToggleSortPopper = useCallback((col: string) => {
    setActiveSortPopper((prev) => (prev === col ? null : col));
  }, []);

  useEffect(() => {
    handleScrollToggle(!!activeFilterColumn || !!activeSortPopper);
  }, [activeFilterColumn, activeSortPopper, handleScrollToggle]);

  useEffect(() => {
    if (!activeFilterColumn) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (
        !target?.closest?.("[data-table-filter-dropdown]") &&
        !target?.closest?.("[data-table-filter-trigger]")
      ) {
        setActiveFilterColumn(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeFilterColumn]);

  useEffect(() => {
    if (!activeSortPopper) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (
        !target?.closest?.("[data-sort-popper]") &&
        !target?.closest?.("[data-sort-trigger]")
      ) {
        setActiveSortPopper(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeSortPopper]);

  const handleAssignDealOwner = useCallback(
    async (contractId: string, ids: string[]) => {
      const assignee = ids?.[0] ?? null;
      await updateDeal({ contractId, body: { assignee } });
      fetchPage(currentPage, rowsPerPage);
    },
    [updateDeal, fetchPage, currentPage, rowsPerPage]
  );

  const columns = useMemo<ColumnDef<Deal>[]>(
    () => [
      {
        accessorKey: "deal_title",
        minSize: 220,
        header: () => (
          <SortableHeader
            label={DEAL_LABELS.name}
            sortField="deal_title"
            activeSortKey={sortKey}
            activeSortOrder={sortOrder}
            onSort={onSort}
            onTogglePopper={handleToggleSortPopper}
            isPopperOpen={activeSortPopper === "deal_title"}
          />
        ),
        cell: ({ row }) => (
          <div className="px-3 max-w-full overflow-hidden">
            <TruncatedTextTooltip
              text={row?.original?.deal_title}
              className="text-md text-verba-indigo dark:text-verba-indigo-dk truncate"
            />
          </div>
        ),
      },
      {
        accessorKey: "contract_end",
        minSize: 200,
        header: () => (
          <SortableHeader
            label="Close Date (GMT +5:30)"
            sortField="contract_end"
            activeSortKey={sortKey}
            activeSortOrder={sortOrder}
            onSort={onSort}
            onTogglePopper={handleToggleSortPopper}
            isPopperOpen={activeSortPopper === "contract_end"}
          />
        ),
        cell: ({ row }) => (
          <div className="px-3 max-w-full overflow-hidden">
            <span className="text-md text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap truncate block">
              {getDateTimeByFormat(new Date(row?.original?.contract_end))?.date}
              {", "}
              {getDateTimeByFormat(new Date(row?.original?.contract_end))?.time}
            </span>
          </div>
        ),
      },
      {
        id: "deal_owner",
        minSize: 200,
        header: () =>
          isViewSelected ? (
            <div className="text-left px-3 py-2">
              <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
                {DEAL_LABELS.owner}
              </span>
            </div>
          ) : (
            <FilterableHeader
              label={DEAL_LABELS.owner}
              filterKey="deal_owner"
              activeFilter={activeFilterColumn}
              onToggleFilter={handleToggleFilter}
            >
              {(triggerRef) => (
                <ContactOwnerFilter
                  triggerRef={triggerRef}
                  selectedAgentIds={sharedOwnerIds}
                  onApply={(ids) => handleOwnerApply(ids)}
                  onReset={() => handleOwnerReset()}
                  onClose={() => setActiveFilterColumn(null)}
                  headingText={DEAL_LABELS.owner}
                />
              )}
            </FilterableHeader>
          ),
        cell: ({ row }) => {
          const assignee = row?.original?.assignee;
          const defaultAgents = assignee?.id
            ? [
                {
                  id: assignee.id,
                  name: assignee.name,
                  role: "",
                  status: "",
                  profile_picture: null,
                  email: "",
                },
              ]
            : [];
          return (
            <div className="px-3" onClick={(e) => e.stopPropagation()}>
              <AgentAssignmentDropdown
                selectedAgentIds={assignee ? [assignee?.id || ""] : []}
                onApply={async (ids) => {
                  await handleAssignDealOwner(
                    row?.original?.contract_id || "",
                    ids
                  );
                }}
                maxAgents={1}
                title={DEAL_LABELS.assignOwnerTitle}
                subHeading={DEAL_LABELS.assignOwnerSubHeading}
                hideRole
                triggerText={DEAL_LABELS.addOwner}
                onToggleScroll={handleScrollToggle}
                defaultSelectedAgents={defaultAgents}
              />
            </div>
          );
        },
      },
      {
        accessorKey: "total_amount",
        minSize: 130,
        header: () => (
          <SortableHeader
            label="Amount"
            sortField="total_amount"
            activeSortKey={sortKey}
            activeSortOrder={sortOrder}
            onSort={onSort}
            onTogglePopper={handleToggleSortPopper}
            isPopperOpen={activeSortPopper === "total_amount"}
          />
        ),
        cell: ({ row }) => (
          <div className="px-3 max-w-full overflow-hidden">
            <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
              {formatDealCurrency(
                row?.original?.total_amount,
                row?.original?.currency
              )}
            </span>
          </div>
        ),
      },
      {
        id: "contact",
        size: 200,
        minSize: 170,
        header: () => (
          <div className="text-left px-3 py-2 flex items-center gap-1">
            <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
              {DEAL_LABELS.singular}
            </span>
            <RightArrowIcon className="w-[14px] h-[14px] text-gray-375 dark:text-gray-375-dk shrink-0" />
            <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
              Contact
            </span>
          </div>
        ),
        cell: ({ row }) => (
          <div className="px-3 flex items-center gap-2 max-w-full overflow-hidden">
            <Avatar text={row?.original?.lead?.name || "-"} size={18} />
            <TruncatedTextTooltip
              text={row?.original?.lead?.name || "-"}
              className="text-md text-verba-indigo dark:text-verba-indigo-dk truncate"
            />
          </div>
        ),
      },
      {
        id: "arrow",
        size: 40,
        header: () => <div />,
        cell: () => (
          <div className="flex items-center justify-center px-2">
            <ChevronRightIcon className="w-4 h-4 text-verba-indigo dark:text-verba-indigo-dk" />
          </div>
        ),
      },
      // {
      //   id: "deal_link",
      //   minSize: 200,
      //   header: () => (
      //     <div className="text-left px-3 py-2 flex items-center gap-1">
      //       <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
      //         Deal
      //       </span>
      //       <RightArrowIcon className="w-[14px] h-[14px] text-gray-375 dark:text-gray-375-dk shrink-0" />
      //       <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
      //         Deals
      //       </span>
      //     </div>
      //   ),
      //   cell: ({ row }) => (
      //     <div className="px-3 flex items-center gap-2 max-w-full overflow-hidden">
      //       <div className="flex items-center justify-center bg-tag-gray dark:bg-tag-gray-dk border border-border-gray dark:border-border-gray-dk rounded-full p-1">
      //         <DealIcon className="w-[14px] h-[14px] text-analytics-gray dark:text-analytics-gray-dk" />
      //       </div>
      //       <TruncatedTextTooltip
      //         text={DUMMY_DEAL_LINKS[row?.index % DUMMY_DEAL_LINKS?.length]}
      //         className="text-md text-verba-indigo dark:text-verba-indigo-dk truncate"
      //       />
      //     </div>
      //   ),
      // },
      // {
      //   id: "deal_link",
      //   minSize: 200,
      //   header: () => (
      //     <div className="text-left px-3 py-2 flex items-center gap-1">
      //       <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
      //         Deal
      //       </span>
      //       <RightArrowIcon className="w-[14px] h-[14px] text-gray-375 dark:text-gray-375-dk shrink-0" />
      //       <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
      //         Deals
      //       </span>
      //     </div>
      //   ),
      //   cell: ({ row }) => (
      //     <div className="px-3 flex items-center gap-2 max-w-full overflow-hidden">
      //       <div className="flex items-center justify-center bg-tag-gray dark:bg-tag-gray-dk border border-border-gray dark:border-border-gray-dk rounded-full p-1">
      //         <DealIcon className="w-[14px] h-[14px] text-analytics-gray dark:text-analytics-gray-dk" />
      //       </div>
      //       <TruncatedTextTooltip
      //         text={DUMMY_DEAL_LINKS[row?.index % DUMMY_DEAL_LINKS?.length]}
      //         className="text-md text-verba-indigo dark:text-verba-indigo-dk truncate"
      //       />
      //     </div>
      //   ),
      // },
      // {
      //   id: "deal_link",
      //   minSize: 200,
      //   header: () => (
      //     <div className="text-left px-3 py-2 flex items-center gap-1">
      //       <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
      //         Deal
      //       </span>
      //       <RightArrowIcon className="w-[14px] h-[14px] text-gray-375 dark:text-gray-375-dk shrink-0" />
      //       <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
      //         Deals
      //       </span>
      //     </div>
      //   ),
      //   cell: ({ row }) => (
      //     <div className="px-3 flex items-center gap-2 max-w-full overflow-hidden">
      //       <div className="flex items-center justify-center bg-tag-gray dark:bg-tag-gray-dk border border-border-gray dark:border-border-gray-dk rounded-full p-1">
      //         <DealIcon className="w-[14px] h-[14px] text-analytics-gray dark:text-analytics-gray-dk" />
      //       </div>
      //       <TruncatedTextTooltip
      //         text={DUMMY_DEAL_LINKS[row?.index % DUMMY_DEAL_LINKS?.length]}
      //         className="text-md text-verba-indigo dark:text-verba-indigo-dk truncate"
      //       />
      //     </div>
      //   ),
      // },
    ],
    [
      activeFilterColumn,
      handleToggleFilter,
      sharedOwnerIds,
      handleOwnerApply,
      handleOwnerReset,
      sortKey,
      sortOrder,
      onSort,
      handleAssignDealOwner,
      handleScrollToggle,
      activeSortPopper,
      handleToggleSortPopper,
      isViewSelected,
    ]
  );

  return (
    <div>
      <div
        className={`flex items-center gap-3 cursor-pointer select-none w-max ${
          !isCollapsed ? "mb-3" : ""
        }`}
        onClick={handleToggleCollapse}
      >
        <CollapseArrowIcon
          className={`w-4 h-4 shrink-0 text-verba-indigo dark:text-verba-indigo-dk transition-transform duration-200 ${
            isCollapsed ? "-rotate-90" : ""
          }`}
        />
        <button
          className="flex items-center gap-2.5 px-3 py-1 rounded-lg"
          style={{ backgroundColor: stage?.secondary_hex_code }}
        >
          <span className="text-md font-semibold text-white tracking-wide uppercase">
            {stage?.stage_title}
          </span>
          <span className="text-sm font-medium text-white">
            {pageTotalCount?.toLocaleString()}
          </span>
        </button>
        {stage?.total_amount && (
          <div className="text-sm text-heading-text dark:text-heading-text-dk border border-border-gray dark:border-border-gray-dk rounded-md px-[6px] py-1 flex items-center gap-1">
            Total amount{" "}
            <span className="font-semibold">{stage.total_amount}</span>
            {stage?.weighted_amount && (
              <>
                {" | "}Weighted amount{" "}
                <span className="font-semibold">{stage.weighted_amount}</span>
                {stage?.weighted_amount_percentage && (
                  <span className="text-analytics-gray dark:text-analytics-gray-dk">
                    {" "}
                    ({stage.weighted_amount_percentage})
                  </span>
                )}
              </>
            )}
            <Tooltip
              content={
                <div className="flex flex-col gap-[10px] min-w-[160px]">
                  <p className="text-sm font-semibold uppercase leading-[18px]">
                    {stage.stage_title}
                  </p>
                  <p className="text-xs font-medium leading-[1.5]">
                    Company currency
                  </p>
                  <div className="flex flex-col gap-[4px]">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-normal leading-[1.5]">
                        Total
                      </span>
                      <span className="text-xs font-semibold leading-[1.5]">
                        {stage.total_amount}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-normal leading-[1.5]">
                        Weighted
                        {stage?.weighted_amount_percentage
                          ? ` (${stage.weighted_amount_percentage})`
                          : ""}
                      </span>
                      <span className="text-xs font-semibold leading-[1.5]">
                        {stage.weighted_amount}
                      </span>
                    </div>
                  </div>
                </div>
              }
              side="top"
              align="center"
              contentClassName="text-xs !px-[14px] !py-[14px]"
            >
              <div>
                <InfoBtnIcon className="w-[18px] h-[18px] text-analytics-gray dark:text-analytics-gray-dk shrink-0" />
              </div>
            </Tooltip>
          </div>
        )}
      </div>

      {!isCollapsed && (
        <>
          {isPageLoading ? (
            <div className="flex items-center justify-center py-8">
              <TableShimmer />
            </div>
          ) : (
            <Table
              COLUMNS={columns}
              COLUMNS_DATA={pageDeals}
              ariaLabel={`${stage?.stage_title?.toUpperCase()} deals table`}
              cellClassName="!p-0 !h-[44px] cursor-pointer"
              rowCount={7}
              onRowClick={(row: Deal) =>
                navigate(`/dashboard/crm/bookings/${row.contract_id}`)
              }
            />
          )}
          {totalPages > 0 && pageDeals?.length > 0 && (
            <div className="mt-3">
              <Pagination
                rowsPerPage={rowsPerPage}
                handleRowsPerPageChange={handleRowsPerPageChange}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
                totalPages={totalPages}
                rowsPerPageText="rows"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DealTableListing;
